// features/workstation-footer.js — WORKSTATION ACTIVITY FOOTER
// ═══════════════════════════════════════════════════════════
// Thin status strip across the bottom of the workstation. Speaks
// to the audience (researchers, lawyers, CPAs) by surfacing what
// the session has produced rather than how long it has run:
//
//   3 decisions · 2 questions open · 4 files · last move 2m ago
//
// Reads directly from the workspace_nodes array (already loaded by
// useWorkspaceNodes in workstation.js). Re-renders every 30s so the
// "last move" relative timestamp stays fresh without spamming React.
// ═══════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { NODE_TYPES, NODE_STATUS, NODE_COLORS } from '../lib/workspace-config';

// Format an ISO timestamp as a compact relative-time string.
// Less than a minute → "just now". Otherwise rounded to the largest unit.
function formatRelative(iso, now) {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '—';
  const diffSec = Math.max(0, Math.floor((now - ts) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

// Format an elapsed duration as "Xh Ym in workspace" or "Xm in workspace"
// for sessions under an hour. Hidden entirely under 1 minute (no point
// showing "0m" right after a workspace opens).
function formatSessionDuration(iso, now) {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return null;
  const diffMin = Math.floor((now - ts) / 60000);
  if (diffMin < 1) return null;
  if (diffMin < 60) return `${diffMin}m in workspace`;
  const hours = Math.floor(diffMin / 60);
  const minutes = diffMin % 60;
  return minutes > 0 ? `${hours}h ${minutes}m in workspace` : `${hours}h in workspace`;
}

export default function WorkstationFooter({ nodes, workspaceCreatedAt }) {
  // Tick every 30s so "last move 2m ago" updates without us touching state
  // anywhere else. Cheap — only this strip re-renders.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  const stats = useMemo(() => {
    const list = nodes || [];
    let decisions = 0;
    let openQuestions = 0;
    let files = 0;
    let lastMoveIso = null;

    list.forEach(n => {
      if (n.type === NODE_TYPES.DECISION) decisions += 1;
      if (n.type === NODE_TYPES.QUESTION && n.status !== NODE_STATUS.PAST) openQuestions += 1;
      if (n.type === NODE_TYPES.FILE) files += 1;

      // "Last move" = most recent created_at across all non-root nodes.
      // Root is created once at workspace open, so excluding it keeps the
      // strip from showing "last move 2m ago" on a brand-new empty session.
      if (n.type !== NODE_TYPES.ROOT && n.created_at) {
        if (!lastMoveIso || new Date(n.created_at).getTime() > new Date(lastMoveIso).getTime()) {
          lastMoveIso = n.created_at;
        }
      }
    });

    return { decisions, openQuestions, files, lastMoveIso };
  }, [nodes]);

  const lastMoveLabel = formatRelative(stats.lastMoveIso, now);
  const sessionDurationLabel = formatSessionDuration(workspaceCreatedAt, now);

  // Color hints reuse the journey-map palette so the footer reads as the
  // same visual language as the map rail above it.
  const colorDecision = NODE_COLORS[NODE_TYPES.DECISION];
  const colorQuestion = NODE_COLORS[NODE_TYPES.QUESTION];
  const colorFile = '#888';

  return (
    <div
      style={{
        height: 28,
        flexShrink: 0,
        borderTop: '1px solid #1e1e2a',
        background: '#07070a',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: 14,
        fontSize: 10,
        fontFamily: 'Georgia, serif',
        color: '#666',
      }}
    >
      <Stat count={stats.decisions} label={stats.decisions === 1 ? 'decision' : 'decisions'} color={colorDecision} />
      <Sep />
      <Stat
        count={stats.openQuestions}
        label={stats.openQuestions === 1 ? 'question open' : 'questions open'}
        color={colorQuestion}
      />
      <Sep />
      <Stat count={stats.files} label={stats.files === 1 ? 'file' : 'files'} color={colorFile} />

      <div style={{ flex: 1 }} />

      {sessionDurationLabel && (
        <>
          <span style={{ color: '#555', fontStyle: 'italic' }}>
            {sessionDurationLabel}
          </span>
          <Sep />
        </>
      )}
      <span style={{ color: '#555', fontStyle: 'italic' }}>
        last move {lastMoveLabel}
      </span>
    </div>
  );
}

function Stat({ count, label, color }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
      <span style={{ color, fontWeight: 700 }}>{count}</span>
      <span>{label}</span>
    </span>
  );
}

function Sep() {
  return <span style={{ color: '#2a2a2a' }}>·</span>;
}
